import { ROUTES } from 'consts'
import { Roles } from 'consts/authConsts'
import { OralHygieneCategoriesByListType, OralHygieneListTypes } from 'consts/oralHygieneReportConsts'
import usePlanLimits from 'hooks/usePlanLimits'
import useQueryParams from 'hooks/useQueryParams'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useFetchOralHygieneAggregationQuery } from 'store/modules/OralHygieneReport'
import { trackEvent } from 'utils/analyticsUtils'
import { isUserOfAnyAdminRole, isUserOfRole } from 'utils/authUtils'
import { isMobile } from 'utils/mobileUtils'
import { getOralHygieneReportPastXDays, setOralHygieneReportPastXDays } from 'utils/storageUtils'
import useCopilotAccountOwnerId from '../../hooks/useCopilotAccountOwnerId'
import useCopilotReports, { CopilotReports, buildCopilotReportPath } from '../../hooks/useCopilotReports'

const DEFAULT_PAST_X_DAYS = 7
const EMPTY_HYGIENE_TREND_COUNTS = Object.fromEntries(
  OralHygieneCategoriesByListType[OralHygieneListTypes.HygieneTrend].map(category => [category.key, 0])
)

export default () => {
  const history = useHistory()
  const enabledReports = useCopilotReports()
  const { grinPlanKey } = usePlanLimits()
  const accountOwnerId = useCopilotAccountOwnerId()
  const { doctor } = useSelector(state => state.profileReducer)
  const { queryParams } = useQueryParams()
  const isDevAdmin = isUserOfRole([Roles.DevAdmin])
  const accountOwnerFeatureFlags = useSelector(state => state.practiceReducer?.accountOwner?.user?.featureFlags?.flags)
  const wasOpenTracked = useRef(false)

  // First visit defaults to 7D; later visits restore the OH-specific selection.
  const [pastXDays, setPastXDaysState] = useState(() => {
    const stored = getOralHygieneReportPastXDays()
    return stored !== undefined ? stored : DEFAULT_PAST_X_DAYS
  })

  const setPastXDays = useCallback(updatedPastXDays => {
    setPastXDaysState(updatedPastXDays)
    setOralHygieneReportPastXDays(updatedPastXDays)
  }, [])

  const {
    data: aggregationData,
    isFetching: isAggregationLoading,
    isUninitialized: isAggregationUninitialized,
    isError: isAggregationError
  } = useFetchOralHygieneAggregationQuery({ accountOwnerId, pastXDays }, { skip: !accountOwnerId })

  useEffect(() => {
    if (isMobile()) {
      history.push(ROUTES.PATIENTS)
      return
    }

    if (isDevAdmin && queryParams.copilotDoctorId) {
      return
    }

    // An admin session resolves its plan from the selected practice context, so without a
    // context grinPlanKey never arrives; treat the plan as resolved and redirect instead of
    // waiting forever, which left the report reachable with the flag off.
    const isPlanResolved = !!grinPlanKey || isUserOfAnyAdminRole()
    const isEligibilityResolved = !!doctor?.id && isPlanResolved && typeof accountOwnerFeatureFlags === 'string'
    if (!isEligibilityResolved) {
      return
    }

    // The report needs only its flag, not an HI plan (GR-10760). A disabled deep link redirects to
    // an enabled sibling report (Copilot when copilotControl is on and the plan is HI), otherwise to Patients.
    if (!enabledReports.some(report => report.key === CopilotReports.OralHygiene)) {
      const [firstEnabledReport] = enabledReports
      history.replace(
        firstEnabledReport
          ? buildCopilotReportPath(firstEnabledReport.route, queryParams.copilotDoctorId)
          : ROUTES.PATIENTS
      )
    }
  }, [isDevAdmin, queryParams, history, enabledReports, doctor?.id, grinPlanKey, accountOwnerFeatureFlags])

  useEffect(() => {
    if (accountOwnerId && !wasOpenTracked.current) {
      wasOpenTracked.current = true
      trackEvent('Oral Hygiene Report - Opened', { pastXDays })
    }
  }, [accountOwnerId, pastXDays])

  const widgetCounts = useMemo(
    () => ({
      hygieneTrend: aggregationData?.hygieneTrend || EMPTY_HYGIENE_TREND_COUNTS,
      highCavityRisk: aggregationData?.dentalRisk?.high || 0,
      highPerioRisk: aggregationData?.perioRisk?.high || 0,
      hygienePayAttention: aggregationData?.hygieneScore?.payAttention || 0
    }),
    [aggregationData]
  )

  return {
    accountOwnerId,
    pastXDays,
    setPastXDays,
    aggregationData,
    widgetCounts,
    isAggregationLoading: isAggregationLoading || isAggregationUninitialized,
    isAggregationError
  }
}
